import React, { useState } from "react"
import { Button, Tooltip } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faArrowDown, faArrowUp, faTrash } from "@fortawesome/free-solid-svg-icons"
import { describeConditionGroups } from "../../model/conditions"
import { RAW_NAMES, ShownNames } from "../../model/shownLabels"
import type { Catalogue } from "../../types/catalogue"
import type { BannerPoint } from "../../types/tabSpecDocument"
import ConditionGroups from "../Inspector/ConditionGroups.component"
import DraftInput from "./DraftInput.component"
import {
  addConditionColumn,
  LAST_COLUMN_STAYS,
  movePoint,
  removePoint,
  renameColumn,
  setColumnCondition,
} from "./bannerEdits"

export interface BannerConditionColumnsProps {
  /** The group's columns: each a condition. */
  points: BannerPoint[]
  onPoints: (points: BannerPoint[]) => void
  /** null while the questionnaire is read: the conditions cannot be edited yet. */
  catalogue: Catalogue | null
  /** The group's heading, unique within the banner: it names the controls. */
  heading: string
  names?: ShownNames
}

/**
 * The columns of a group of conditions: each with its name, its condition
 * in words, and the condition editor (`ConditionGroups`) under it while it
 * is open, one column at a time. A group keeps at least one column. A name
 * writes when its field is left.
 */
const BannerConditionColumns: React.FC<BannerConditionColumnsProps> = ({
  points,
  onPoints,
  catalogue,
  heading,
  names = RAW_NAMES,
}) => {
  /** The column whose condition is open, by position. */
  const [open, setOpen] = useState<number | null>(null)
  const last = points.length === 1

  const commitName = (index: number, text: string): string | null => {
    const next = renameColumn(points, index, text)
    if (typeof next === "string") return next
    onPoints(next)
    return null
  }

  return (
    <div className="ts-banner-group__body">
      <p className="ts-hint">Each column counts the respondents for whom its condition holds.</p>
      <ul className="ts-banner-group__points" aria-label={`Columns of ${heading}`}>
        {points.map((p, i) => {
          if (p.kind !== "condition") return null
          const editing = open === i
          const words = catalogue ? describeConditionGroups(p.condition, names, catalogue) : null
          return (
            // The position is part of the key: a stored list may name a column twice.
            <li key={`${i}:${p.name}`} className="ts-banner-point ts-banner-point--condition">
              <DraftInput
                className="ts-banner-point__column"
                aria-label={`Name of ${p.name} in ${heading}`}
                placeholder="Column name"
                value={p.name}
                onCommit={(text) => commitName(i, text)}
              />
              <span className="ts-banner-point__words">{words ?? "No condition yet."}</span>
              <Button
                size="small"
                aria-expanded={editing}
                aria-label={`${editing ? "Close" : "Edit"} the condition of ${p.name} in ${heading}`}
                disabled={!catalogue}
                onClick={() => setOpen(editing ? null : i)}
              >
                {editing ? "Done" : "Edit"}
              </Button>
              <Button
                size="small"
                type="text"
                aria-label={`Move ${p.name} up in ${heading}`}
                icon={<FontAwesomeIcon icon={faArrowUp} />}
                disabled={i === 0}
                onClick={() => {
                  setOpen(null)
                  onPoints(movePoint(points, i, i - 1))
                }}
              />
              <Button
                size="small"
                type="text"
                aria-label={`Move ${p.name} down in ${heading}`}
                icon={<FontAwesomeIcon icon={faArrowDown} />}
                disabled={i === points.length - 1}
                onClick={() => {
                  setOpen(null)
                  onPoints(movePoint(points, i, i + 1))
                }}
              />
              <Tooltip title={last ? LAST_COLUMN_STAYS : null}>
                <span>
                  <Button
                    size="small"
                    type="text"
                    danger
                    aria-label={`Remove ${p.name} from ${heading}`}
                    icon={<FontAwesomeIcon icon={faTrash} />}
                    disabled={last}
                    onClick={() => {
                      setOpen(null)
                      onPoints(removePoint(points, i))
                    }}
                  />
                </span>
              </Tooltip>
              {editing && catalogue && (
                <div className="ts-banner-point__condition">
                  <ConditionGroups
                    value={p.condition}
                    onChange={(next) => onPoints(setColumnCondition(points, i, next))}
                    catalogue={catalogue}
                    names={names}
                  />
                </div>
              )}
            </li>
          )
        })}
      </ul>
      <div className="ts-banner-group__actions">
        <Button size="small" onClick={() => onPoints(addConditionColumn(points))}>
          Add column
        </Button>
      </div>
    </div>
  )
}

export default BannerConditionColumns
